/**
 * Resolving `pb-vendored://` URIs for the two surfaces that render CSS
 * directly rather than packaging it: the live preview iframe (R2/manifest URL)
 * and the saved preview bundle (inlined data: URI, because that document is
 * served from another origin where a cross-origin font request is a CORS
 * question we should not be asking).
 *
 * The export path does NOT use this module — it routes the same URIs through
 * `export/asset-resolver.js` so the binaries land in the ZIP.
 */

import { getAssetURLSync, loadAsset } from '../core/asset-loader.js';
import { collectVendoredUris, parseVendoredUri } from './vendored-asset-uri.js';

/**
 * Replace every `pb-vendored://` URI with its manifest-resolved asset URL.
 * Synchronous because `assembleDocument()` is: the manifest is already cached
 * by the time any template renders, and `getAssetURLSync` kicks off the load
 * and falls back to the unhashed key when it is not.
 */
export function resolveVendoredUrisSync(css) {
  const source = String(css || '');
  const uris = collectVendoredUris(source);
  if (uris.length === 0) return source;
  let out = source;
  for (const uri of uris) {
    const parsed = parseVendoredUri(uri);
    if (!parsed) continue;
    out = out.split(uri).join(getAssetURLSync(parsed.logicalKey));
  }
  return out;
}

const MIME_BY_EXT = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
  eot: 'application/vnd.ms-fontobject',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  bmp: 'image/bmp'
};

function mimeForVendoredPath(relPath) {
  const ext = String(relPath || '')
    .split('.')
    .pop()
    .toLowerCase();
  return MIME_BY_EXT[ext] || 'application/octet-stream';
}

async function blobToDataUri(blob, mime) {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return `data:${mime};base64,${btoa(binary)}`;
}

/**
 * Replace every `pb-vendored://` URI with an inlined data: URI.
 *
 * A URI that cannot be fetched is left in place and reported. The browser skips
 * a face whose url() does not load, which is the same outcome as deleting it —
 * but the warning is the part the caller can act on, and a silent deletion here
 * would look exactly like the strip behaviour this phase exists to remove.
 *
 * @param {string} css
 * @param {string[]} [warnings] - appended to on failure
 * @returns {Promise<string>}
 */
export async function inlineVendoredUris(css, warnings = []) {
  const source = String(css || '');
  const uris = collectVendoredUris(source);
  if (uris.length === 0) return source;
  let out = source;
  for (const uri of uris) {
    const parsed = parseVendoredUri(uri);
    if (!parsed) continue;
    try {
      const resp = await loadAsset(parsed.logicalKey);
      const blob = await resp.blob();
      if (!blob || blob.size === 0) throw new Error('empty vendored asset');
      out = out.split(uri).join(await blobToDataUri(blob, mimeForVendoredPath(parsed.relPath)));
    } catch (err) {
      warnings.push(`Could not inline vendored asset ${uri}: ${err.message || err}`);
    }
  }
  return out;
}
